import { useState } from 'react';
import { Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { apiFetch } from '../utils/apiClient';
import PasswordInput from '../components/PasswordInput';
import { getPasswordPolicyError, PASSWORD_POLICY_MESSAGE } from '../utils/passwordPolicy';
import { buildMarketingSignupField, getMarketingVisitorId } from '../utils/marketingAttribution';

// API base URL - configurable via environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

function Signup({ isAuthenticated }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordPolicyError, setPasswordPolicyError] = useState('');
  const [error, setError] = useState('');
  const [accountExists, setAccountExists] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAccountExists(false);

    const policyError = getPasswordPolicyError(password);
    if (policyError) {
      setPasswordPolicyError(policyError);
      setError(policyError);
      return;
    }
    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      const response = await apiFetch(`${API_BASE_URL}/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password,
          ...buildMarketingSignupField(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : 'Please check the form and try again.'
        );
      }

      const data = await response.json();
      if (data.status === 'account_exists') {
        setAccountExists(true);
        setError('An account with this email already exists.');
        return;
      }
      const searchParams = new URLSearchParams(location.search);
      const plan = searchParams.get('plan');
      const billing = searchParams.get('billing');
      const afterAuthPath = plan === 'pro' || plan === 'premium'
        ? `/billing/checkout?plan=${plan}&billing=${billing || 'monthly'}`
        : '/dashboard';
      if (data.status === "email_verification_required") {
        navigate("/verify-email", {
          replace: true,
          state: { email, afterAuthPath, marketingRegistration: Boolean(getMarketingVisitorId()) },
        });
        return;
      }
      throw new Error("Unable to start email verification");
    } catch (err) {
      setAccountExists(false);
      setError(err.message);
    }
  };

  const switchToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="public-page">
      {/* Navigation - Premium Compact Header */}
      <nav className="public-nav">
        <div className="public-nav-container">
          <Link to="/" className="public-nav-brand">
            <img 
              src="/assets/black_peak_analytics_logo_transparent_240.png" 
              alt="Black Peak Analytics" 
              className="nav-brand-logo" 
            />
          </Link>
          <div className="public-nav-links">
            <Link to="/" className="public-nav-link">Home</Link>
            <Link to="/pricing" className="public-nav-link">Pricing</Link>
          </div>
        </div>
      </nav>

      {/* Signup Section */}
      <div className="login-section">
        <div className="login-container">
          <div className="login-card">
            <h1 className="login-title">Create Account</h1>
            <p className="login-subtitle">
              Join Black Peak Analytics and start finding value beyond the price
            </p>

            {error && (
              <div className="login-error">
                {error}
                {accountExists && (
                  <> <Link className="login-signup-link" to="/forgot-password" state={{ email }}>Forgot password?</Link></>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form">
              <div className="login-form-group">
                <label className="login-label" htmlFor="signup-page-email">Email</label>
                <input
                  id="signup-page-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="login-input"
                />
              </div>

              <div className="login-form-group">
                <label className="login-label" htmlFor="signup-page-password">Password</label>
                <PasswordInput
                  id="signup-page-password"
                  label="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={password}
                  onChange={(event) => {
                    const nextPassword = event.target.value;
                    setPassword(nextPassword);
                    const nextPolicyError = getPasswordPolicyError(nextPassword);
                        setPasswordPolicyError(nextPolicyError);
                        if (!nextPolicyError) setError('');
                  }}
                  required
                />
                <p className="password-policy-hint">{PASSWORD_POLICY_MESSAGE}</p>
                {passwordPolicyError && <p className="password-policy-error" role="alert">{passwordPolicyError}</p>}
              </div>

              <div className="login-form-group">
                <label className="login-label" htmlFor="signup-page-confirm-password">Confirm Password</label>
                <PasswordInput
                  id="signup-page-confirm-password"
                  label="confirm password"
                  autoComplete="new-password"
                  minLength={8}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  required
                />
              </div>

              <button type="submit" className="login-button">
                Create Account
              </button>
            </form>

            <div className="login-footer">
              <p className="login-footer-text">
                Already have an account?{' '}
                <button 
                  onClick={switchToLogin}
                  className="login-signup-link"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                >
                  Sign in
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
